import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Users, Calendar, Clock, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/Button';
interface BookTableModalProps {
  onClose: () => void;
}
type Step = 'meal-selection' | 'booking-form' | 'success';
type MealType = 'Frukost' | 'Fika' | 'Lunch' | 'AW';
const mealOptions: {
  type: MealType;
  description: string;
}[] = [{
  type: 'Frukost',
  description: 'Starta dagen på bästa sätt med nybakat.'
}, {
  type: 'Fika',
  description: 'Kaffe och något sött från vårt hantverksbageri.'
}, {
  type: 'Lunch',
  description: 'Vällagad mat från grunden.'
}, {
  type: 'AW',
  description: 'Avsluta dagen med god dryck och tilltugg.'
}];
export function BookTableModal({
  onClose
}: BookTableModalProps) {
  const [step, setStep] = useState<Step>('meal-selection');
  const [selectedMeal, setSelectedMeal] = useState<MealType | null>(null);
  // Form state
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [guests, setGuests] = useState(2);
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const handleMealSelect = (meal: MealType) => {
    setSelectedMeal(meal);
    setStep('booking-form');
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    const emailSubject = `[BOKA BORD] ${selectedMeal} – ${name} – ${date} kl ${time}`;
    const emailMessage = [`════════════════════════════════`, `   NY BORDSBOKNING`, `════════════════════════════════`, '', `▸ MÅLTID:     ${selectedMeal}`, `▸ DATUM:      ${date}`, `▸ TID:        ${time}`, `▸ GÄSTER:     ${guests} st`, '', `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `   KONTAKTUPPGIFTER`, `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `  Namn:     ${name}`, `  Telefon:  ${phone}`, `  E-post:   ${email}`, '', `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `   ÖNSKEMÅL / MEDDELANDE`, `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━`, `  ${message || 'Inga särskilda önskemål'}`, '', `════════════════════════════════`, `Svara direkt på detta mejl för att nå gästen.`, `════════════════════════════════`].join('\n');
    try {
      const emailRes = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          access_key: '4fe1d253-ce1a-4a03-b6ab-6294cf0e6251',
          subject: emailSubject,
          from_name: `${name} <${email}>`,
          replyto: email,
          message: emailMessage
        })
      });
      if (!emailRes.ok) throw new Error('Kunde inte skicka bokning');
      setStep('success');
    } catch (err) {
      setError('Något gick fel. Vänligen försök igen eller ring oss på 0470-52 36 03.');
    } finally {
      setIsSubmitting(false);
    }
  };
  return <AnimatePresence data-id="element-18" data-mp="utwxvm1ncf4o">
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm overflow-y-auto" onClick={onClose} data-id="element-19" data-mp="zqx5w2i23ryf">
        <motion.div initial={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} exit={{
        scale: 0.95,
        opacity: 0,
        y: 20
      }} onClick={e => e.stopPropagation()} className="bg-[#F2EDE4] rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden my-8" data-id="element-20" data-mp="1di1yikn0bjz9">
          {/* Header */}
          <div className="bg-white px-4 md:px-6 py-3 md:py-4 border-b border-[#7C8062]/15 flex items-center justify-between sticky top-0 z-10" data-id="element-21" data-mp="2k8o7z8p0hhgt">
            <div className="flex items-center gap-2 md:gap-3 min-w-0" data-id="element-22" data-mp="2yvzkpdwxj7ii">
              {step === 'booking-form' && <button onClick={() => setStep('meal-selection')} className="flex items-center gap-1.5 px-3 py-2 -ml-2 text-sm font-medium text-[#7C8062] hover:text-[#1A1A1A] hover:bg-[#F2EDE4] rounded-lg transition-colors shrink-0" aria-label="Tillbaka" data-id="element-23" data-mp="2u022t360pt3m">
                  <ArrowLeft className="w-4 h-4" data-id="element-24" data-mp="2b6oo88mm3za6" />
                  <span data-id="element-25" data-mp="2b5wvd5kegfnl">Tillbaka</span>
                </button>}
              <h2 className="text-lg md:text-xl font-serif text-[#1A1A1A] uppercase tracking-wide truncate" data-id="element-26" data-mp="3qmnf8743len7">
                Boka Bord
              </h2>
            </div>
            <button onClick={onClose} className="p-2 -mr-2 text-[#1A1A1A]/50 hover:text-[#1A1A1A] hover:bg-[#F2EDE4] rounded-full transition-colors shrink-0" aria-label="Stäng" data-id="element-27" data-mp="3u5cruxvy0bqn">
              <X className="w-5 h-5" data-id="element-28" data-mp="3k99j396l5euq" />
            </button>
          </div>

          <div className="p-6 md:p-8" data-id="element-29" data-mp="3p7uix7hxdwin">
            {step === 'meal-selection' && <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: 20
          }} className="space-y-6" data-id="element-30" data-mp="gqg9t8kubc6d">
                <div className="space-y-2" data-id="element-31" data-mp="zpu2zoaqwivt">
                  <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-32" data-mp="zp2a4l8j8z98">
                    Varmt välkommen att boka bord.
                  </h3>
                  <p className="text-[#1A1A1A]/70 text-sm" data-id="element-33" data-mp="1eckh5q9ki5yt">
                    Notera att vi har ett par platser för drop in-gäster.
                  </p>
                </div>

                <div className="space-y-3" data-id="element-34" data-mp="19e9a1nanq9ka">
                  {mealOptions.map(meal => <button key={meal.type} onClick={() => handleMealSelect(meal.type)} className="w-full text-left p-5 bg-white border border-[#7C8062]/20 rounded-xl hover:border-[#7C8062] hover:shadow-md transition-all group" data-id="element-35" data-mp="15a1hff12w9gs">
                      <h4 className="text-lg font-serif text-[#1A1A1A] mb-1 group-hover:text-[#7C8062] transition-colors" data-id="element-36" data-mp="ql5bbnil16it">
                        {meal.type}
                      </h4>
                      <p className="text-sm text-[#1A1A1A]/60" data-id="element-37" data-mp="ho4nbhzfdafw">
                        {meal.description}
                      </p>
                    </button>)}
                </div>

                <div className="p-4 bg-[#7C8062]/5 rounded-xl border border-[#7C8062]/15 space-y-2" data-id="element-38" data-mp="7q73d6ollzt3">
                  <p className="text-sm font-medium text-[#1A1A1A]" data-id="element-39" data-mp="2jdk0p9wnnfba">
                    🏠 Vill du abonnera en del av eller hela lokalen?
                  </p>
                  <p className="text-xs text-[#1A1A1A]/70 leading-relaxed" data-id="element-40" data-mp="2taon9auexcsm">
                    Mejla{' '}
                    <a href="mailto:bokabord@topnotchbakery.se?subject=Abonering" className="text-[#7C8062] font-semibold underline underline-offset-2 hover:text-[#6A6D53] transition-colors" data-id="element-41" data-mp="2x9xm2u4b80v1">
                      bokabord@topnotchbakery.se
                    </a>{' '}
                    med ändamålet i ämnesraden, t.ex.{' '}
                    <span className="italic text-[#1A1A1A]/90" data-id="element-42" data-mp="2ed64qq8bvvtm">
                      "Abonering Frukost"
                    </span>
                    ,{' '}
                    <span className="italic text-[#1A1A1A]/90" data-id="element-43" data-mp="1lvtqjji2q4nv">
                      "Abonering AW"
                    </span>{' '}
                    osv. Skriv gärna i mejlet hur många ni är, vilket datum och
                    vilka tider det gäller.
                  </p>
                </div>

                <button onClick={onClose} className="w-full py-3 text-sm font-medium text-[#1A1A1A]/50 hover:text-[#1A1A1A] transition-colors" data-id="element-44" data-mp="p6no1wrns61g">
                  Stäng
                </button>
              </motion.div>}

            {step === 'booking-form' && <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} data-id="element-45" data-mp="t7hccr90jmuv">
                <div className="mb-6 pb-4 border-b border-[#7C8062]/15" data-id="element-46" data-mp="1gw0f6r9an7fk">
                  <h3 className="text-xl font-serif text-[#1A1A1A] mb-1" data-id="element-47" data-mp="1cy2uo1qgx9ii">
                    Boka {selectedMeal}
                  </h3>
                  <p className="text-sm text-[#1A1A1A]/60" data-id="element-48" data-mp="20jwnmvhfcpu7">
                    Fyll i dina uppgifter nedan för att slutföra bokningen.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-id="element-49" data-mp="1mm84bs309qr9">
                  <div className="space-y-4" data-id="element-50" data-mp="1rhvqrzq3s5t2">
                    <div data-id="element-51" data-mp="1cuapmbcsggns">
                      <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-52" data-mp="1cu2cqjgo1bcl">
                        Namn *
                      </label>
                      <input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" placeholder="För- och efternamn" data-id="element-53" data-mp="1hn3wb4rthz2i" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-54" data-mp="370q89o7pa7zb">
                      <div data-id="element-55" data-mp="3b1if6at3o0sf">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-56" data-mp="3axmcnuunkt7s">
                          Telefon *
                        </label>
                        <input type="tel" required value={phone} onChange={e => setPhone(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" placeholder="070-123 45 67" data-id="element-57" data-mp="31zsqvduilhyv" />
                      </div>
                      <div data-id="element-58" data-mp="cg8kxcbvmex2">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-59" data-mp="h8ry486nj81c">
                          E-post *
                        </label>
                        <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full h-11 px-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" placeholder="din@epost.se" data-id="element-60" data-mp="21xmb2uwwwl9d" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-61" data-mp="1s110zv61cjyo">
                      <div data-id="element-62" data-mp="1rywsezx6yws2">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-63" data-mp="1n1ohnkedhv0u">
                          Antal gäster *
                        </label>
                        <div className="relative" data-id="element-64" data-mp="33i9aguvjgq9p">
                          <input type="number" min="1" max="20" required value={guests} onChange={e => setGuests(parseInt(e.target.value) || 1)} className="w-full h-11 pl-10 pr-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" data-id="element-65" data-mp="2ygbklhsmux7s" />
                          <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-66" data-mp="1hfu9akkmic6f" />
                        </div>
                      </div>
                      <div data-id="element-67" data-mp="1r5vtv2drpcgl">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-68" data-mp="1r65f6cnvavfy">
                          Datum *
                        </label>
                        <div className="relative" data-id="element-69" data-mp="ynhvn1oirogz">
                          <input type="date" required value={date} onChange={e => setDate(e.target.value)} className="w-full h-11 pl-10 pr-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" data-id="element-70" data-mp="ym6rv1v2qgpv" />
                          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-71" data-mp="877nw6gysnwy" />
                        </div>
                      </div>
                      <div data-id="element-72" data-mp="3umshmjtznbqy">
                        <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-73" data-mp="3um0ln7whbc05">
                          Tid *
                        </label>
                        <div className="relative" data-id="element-74" data-mp="3llc5a6jm4zk3">
                          <input type="time" required value={time} onChange={e => setTime(e.target.value)} className="w-full h-11 pl-10 pr-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm" data-id="element-75" data-mp="3ln5bxlu87js3" />
                          <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1A1A1A]/40 pointer-events-none" data-id="element-76" data-mp="1fr31lyy7pcix" />
                        </div>
                      </div>
                    </div>

                    <div data-id="element-77" data-mp="15uqc7d9p9ktz">
                      <label className="block text-xs font-medium text-[#1A1A1A]/70 mb-1.5" data-id="element-78" data-mp="15ruareyn8e0s">
                        Önskemål / Meddelande
                      </label>
                      <textarea value={message} onChange={e => setMessage(e.target.value)} className="w-full min-h-[80px] p-3 rounded-lg border border-[#7C8062]/20 bg-white focus:border-[#7C8062] focus:ring-2 focus:ring-[#7C8062]/20 outline-none transition-all text-sm resize-y" placeholder="Allergier, barnvagn, eller andra önskemål..." data-id="element-79" data-mp="wu1v29p0f4cv" />
                    </div>
                  </div>

                  {error && <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg border border-red-100" data-id="element-80" data-mp="3a5cy0avgri6r">
                      {error}
                    </div>}

                  <div className="pt-4" data-id="element-81" data-mp="3k3stsr5frfml">
                    <Button type="submit" isLoading={isSubmitting} className="w-full py-3.5 text-base shadow-sm" data-id="element-82" data-mp="3f34tfevbhmfp">
                      Skicka bokningsförfrågan
                    </Button>
                    <p className="text-center text-xs text-[#1A1A1A]/50 mt-3" data-id="element-83" data-mp="1yodadsk29rj2">
                      Vi återkommer med en bekräftelse så snart vi kan.
                    </p>
                  </div>
                </form>
              </motion.div>}

            {step === 'success' && <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center py-8 space-y-4" data-id="element-84" data-mp="2l8k4g8otham">
                <div className="w-16 h-16 bg-[#7C8062]/10 rounded-full flex items-center justify-center mx-auto mb-2" data-id="element-85" data-mp="gl9j1q5e6eng">
                  <CheckCircle2 className="w-8 h-8 text-[#7C8062]" data-id="element-86" data-mp="ljb0y7r307xc" />
                </div>
                <h3 className="text-2xl font-serif text-[#1A1A1A]" data-id="element-87" data-mp="qa9mpmuu9y7z">
                  Tack för din förfrågan!
                </h3>
                <p className="text-[#1A1A1A]/70 max-w-sm mx-auto" data-id="element-88" data-mp="v8kxbzsykcr9">
                  Vi har tagit emot din bokningsförfrågan för {selectedMeal} den{' '}
                  {date} kl {time}. Vi återkommer till dig via e-post eller
                  telefon med en bekräftelse.
                </p>
                <div className="pt-6" data-id="element-89" data-mp="2otefg1f4bv9x">
                  <Button onClick={onClose} className="w-full sm:w-auto px-8" data-id="element-90" data-mp="2oqa3uizbpx98">
                    Stäng
                  </Button>
                </div>
              </motion.div>}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>;
}